import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import './BookingPage.css';

const STEPS = ['Your Info', 'Payment', 'Ticket'];

export default function BookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ fullName: '', phoneNumber: '', couponCode: '' });
  const [couponStatus, setCouponStatus] = useState(null); // { valid, message }
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ticketData, setTicketData] = useState(null); // { ticketId, ticketNumber, isFreeTicket, qrCodeData }
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    api.get('/admin/payment-info').then(r => setPaymentInfo(r.data)).catch(() => {});
  }, []);

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === 'couponCode') setCouponStatus(null);
    setError('');
  };

  const verifyCoupon = async () => {
    if (!form.couponCode.trim()) return;
    setCheckingCoupon(true);
    try {
      const res = await api.post('/coupons/verify', { code: form.couponCode.trim() });
      setCouponStatus(res.data);
    } catch {
      setCouponStatus({ valid: false, message: 'Could not verify coupon' });
    } finally {
      setCheckingCoupon(false);
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.fullName.trim()) return setError('Please enter your full name');
    if (!form.phoneNumber.trim()) return setError('Please enter your phone number');

    // If coupon typed but not verified, verify first
    if (form.couponCode.trim() && !couponStatus) {
      return setError('Please click "Apply" to verify your coupon code');
    }

    setLoading(true);
    try {
      const res = await api.post('/tickets/book', form);
      setTicketData(res.data);
      if (res.data.isFreeTicket) {
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setReceipt(file);
    setReceiptPreview(URL.createObjectURL(file));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!receipt) return setError('Please attach your payment receipt');
    setLoading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('receipt', receipt);
      await api.post(`/tickets/${ticketData.ticketId}/payment`, fd);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const stepStatus = (n) => {
    if (n < step) return 'done';
    if (n === step) return 'active';
    return '';
  };

  return (
    <div className="bp">
      {/* Back */}
      <div className="bp-nav">
        <button className="bp-back" onClick={() => navigate('/')}>← Back to Event</button>
      </div>

      {/* Progress */}
      <div className="bp-progress">
        <div className="bp-steps">
          {STEPS.map((label, i) => (
            <React.Fragment key={i}>
              <div className={`bp-step ${stepStatus(i + 1)}`}>
                <div>
                  <div className="bp-step-circle">
                    {step > i + 1 ? '✓' : i + 1}
                  </div>
                  <span className="bp-step-label">{label}</span>
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`bp-step ${stepStatus(i + 1)}`} style={{ flex: 1 }}>
                  <div className="bp-step-line" style={{ flex: 1, margin: '0 4px', marginBottom: 20 }} />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ─── Step 1: Personal Info ─── */}
      {step === 1 && (
        <div className="bp-card">
          <h2 className="bp-card-title">Your Information</h2>
          <p className="bp-card-sub">Fill in your details to reserve your seat</p>
          {error && <div className="bp-error">{error}</div>}
          <form onSubmit={handleStep1Submit}>
            <div className="bp-field">
              <label className="bp-label">Full Name *</label>
              <input className="bp-input" name="fullName" placeholder="e.g. Sokha Chan" value={form.fullName} onChange={handleChange} />
            </div>
            <div className="bp-field">
              <label className="bp-label">Phone Number *</label>
              <input className="bp-input" name="phoneNumber" placeholder="e.g. 012 345 678" value={form.phoneNumber} onChange={handleChange} />
            </div>
            <div className="bp-field">
              <label className="bp-label">Coupon Code <span style={{ fontWeight: 400, opacity: 0.5 }}>(optional)</span></label>
              <div className="bp-coupon-row">
                <input
                  className="bp-input" name="couponCode" placeholder="Enter coupon code"
                  value={form.couponCode} onChange={handleChange}
                  style={{ textTransform: 'uppercase' }}
                />
                <button type="button" className="bp-coupon-apply" onClick={verifyCoupon} disabled={checkingCoupon || !form.couponCode.trim()}>
                  {checkingCoupon ? '...' : 'Apply'}
                </button>
              </div>
              {couponStatus && (
                <div className={`bp-coupon-msg ${couponStatus.valid ? 'valid' : 'invalid'}`}>
                  {couponStatus.valid ? '✓ ' : '✗ '}{couponStatus.message}
                </div>
              )}
            </div>
            <button className="bp-btn" type="submit" disabled={loading}>
              {loading ? 'Processing...' : 'Continue →'}
            </button>
          </form>
        </div>
      )}

      {/* ─── Step 2: Payment ─── */}
      {step === 2 && (
        <div className="bp-card">
          <h2 className="bp-card-title">Complete Payment</h2>
          <p className="bp-card-sub">Scan the QR code below and send your payment receipt</p>
          {error && <div className="bp-error">{error}</div>}

          <div className="bp-payment-info">
            {paymentInfo?.bankQRImage ? (
              <img
                src={`/uploads/bankqr/${paymentInfo.bankQRImage}`}
                alt="Bank QR Code"
                className="bp-bank-qr"
              />
            ) : (
              <div className="bp-bank-qr-placeholder">
                🏦<br />Bank QR Code<br />(Admin will upload)
              </div>
            )}
            {paymentInfo?.bankName && <h4>{paymentInfo.bankName}</h4>}
            {paymentInfo?.accountName && <p>Account: {paymentInfo.accountName}</p>}
            {paymentInfo?.accountNumber && <p>Number: {paymentInfo.accountNumber}</p>}
          </div>

          <form onSubmit={handlePaymentSubmit}>
            <div className="bp-field">
              <label className="bp-label">Attach Payment Receipt *</label>
              <div className="bp-upload-area" onClick={() => fileRef.current.click()}>
                <div className="bp-upload-icon">{receipt ? '✅' : '📎'}</div>
                <div className="bp-upload-text">
                  {receipt ? <strong>{receipt.name}</strong> : <><strong>Click to browse</strong> or drag your screenshot here</>}
                </div>
                {receiptPreview && <img src={receiptPreview} alt="receipt" className="bp-upload-preview" />}
              </div>
              <input ref={fileRef} type="file" className="bp-upload-input" accept="image/*" onChange={handleFileChange} />
            </div>
            <button className="bp-btn" type="submit" disabled={loading || !receipt}>
              {loading ? 'Submitting...' : 'Submit & Get My Ticket'}
            </button>
          </form>
          <button className="bp-btn-outline" onClick={() => setStep(1)}>← Edit Info</button>
        </div>
      )}

      {/* ─── Step 3: Ticket ─── */}
      {step === 3 && ticketData && (
        <div className="bp-card" style={{ textAlign: 'center' }}>
          <span className="bp-success-icon">🎉</span>
          <h2 className="bp-card-title">You're Registered!</h2>
          <p className="bp-card-sub">
            {ticketData.isFreeTicket
              ? 'Free ticket confirmed — see you at the event!'
              : 'Receipt received — pending admin approval.'}
          </p>
          <button
            className="bp-btn"
            style={{ maxWidth: 280, margin: '24px auto 0' }}
            onClick={() => navigate(`/ticket/${ticketData.ticketNumber}`)}
          >
            View My Ticket →
          </button>
        </div>
      )}
    </div>
  );
}
